import { useState } from "react";
import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, CompareTable, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { LeadForm } from "../../components/LeadForm";
import { waLink, IMAGES } from "../../data/site";
import { Sun, TrendingDown, KeyRound, Ban, HardHat, CalendarClock } from "lucide-react";

const fmt = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const WA_MSG = "Vim pelo site. Quero energia solar sem investimento.";

const FAQ = [
  { q: "Vou pagar alguma coisa pela instalação?", a: "Não. Você não paga nada na adesão nem pela instalação: o fundo de investimento compra o equipamento, instala e homologa as placas. Você paga uma mensalidade, sempre menor que a sua conta de luz atual." },
  { q: "As placas ficam mesmo comigo no fim?", a: "Sim. Ao fim do contrato, as placas e o inversor passam a ser 100% seus, sem custo de transferência, e a mensalidade acaba." },
  { q: "Para quem eu pago a mensalidade?", a: "Para o fundo de investimento que financiou as placas. A conta da distribuidora continua chegando, só com as taxas obrigatórias, os impostos e o que passar da geração." },
  { q: "Quanto tempo dura o contrato?", a: "6, 8, 10 ou 12 anos. O prazo é liberado pelo fundo conforme o seu perfil e vem escrito na proposta." },
  { q: "A mensalidade aumenta?", a: "Uma vez por ano, só pela inflação (IPCA). A conta de luz, em média, sobe mais que isso, então a economia tende a crescer com o tempo." },
  { q: "Quando começo a pagar?", a: "Só depois que a distribuidora troca o medidor e as placas começam a gerar. Até lá você paga só a sua conta de luz normal." },
  { q: "Precisa de banco ou de entrada?", a: "Não. A análise de crédito é do fundo, não de banco, e não existe entrada nem financiamento bancário." },
  { q: "Posso cancelar?", a: "Não há cancelamento simples: ou o contrato é cumprido ou é quitado de uma vez. Por isso eu explico todas as condições antes de você assinar." },
  { q: "Moro em apartamento. Posso?", a: "Na Conexão Solar, não. Para o seu apartamento, o caminho é o desconto na conta de luz por energia de usinas parceiras; para a área comum do condomínio, as Placas solares." },
];

const SolarSimulator = () => {
  const [bill, setBill] = useState(600);
  const [years, setYears] = useState(6);
  const pct = ((bill - 250) / (3000 - 250)) * 100;
  const firstMonthSaving = bill * 0.22;
  let total = 0, conta = bill, mens = bill * 0.78;
  for (let y = 0; y < years; y++) {
    total += (conta - mens) * 12;
    conta *= 1.085;
    mens *= 1.05;
  }
  return (
    <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 backdrop-blur-md p-7 sm:p-9" data-testid="solar-simulator">
      <p className="text-sm font-semibold text-white mb-1.5">Valor médio da sua conta de luz</p>
      <p className="font-heading text-4xl sm:text-5xl font-extrabold text-[#00E676]">{fmt(bill)}</p>
      <input
        type="range" min="250" max="3000" step="10" value={bill}
        onChange={(e) => setBill(parseFloat(e.target.value))}
        className="savings-range mt-7" style={{ "--fill": `${pct}%` }}
        data-testid="solar-simulator-slider" aria-label="Valor médio da conta de luz"
      />
      <div className="flex justify-between text-[11px] font-mono2 text-emerald-100/40 mt-2">
        <span>R$ 250</span><span>R$ 3.000+</span>
      </div>
      <div className="mt-6 flex gap-2">
        {[1, 3, 6].map((y) => (
          <button
            key={y}
            data-testid={`solar-period-${y}`}
            onClick={() => setYears(y)}
            className={`rounded-full text-xs font-bold px-5 py-2.5 border transition-colors duration-300 ${years === y ? "bg-[#00E676] text-[#04120E] border-[#00E676]" : "border-white/15 text-emerald-100/60 hover:bg-white/5"}`}
          >
            {y} {y === 1 ? "ano" : "anos"}
          </button>
        ))}
      </div>
      <div className="mt-7 border-t border-white/10 pt-6">
        <p className="text-sm text-emerald-100/70 leading-relaxed">
          Economia estimada de <strong className="text-white">{fmt(total)}</strong> em {years} {years === 1 ? "ano" : "anos"} com a Conexão Solar
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-[#04120E]/70 border border-white/10 p-4">
            <p className="text-xs text-emerald-100/50">Instalação</p>
            <p className="font-heading text-xl font-bold text-white mt-1">R$ 0</p>
          </div>
          <div className="rounded-xl bg-[#00E676]/10 border border-[#00E676]/25 p-4">
            <p className="text-xs text-emerald-100/50">1º mês</p>
            <p className="font-heading text-xl font-bold text-[#00E676] mt-1" data-testid="solar-first-month">{fmt(firstMonthSaving)} a menos</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-emerald-100/55 leading-relaxed">
          No fim do contrato, as placas são suas e a mensalidade acaba: você passa a pagar só a taxa mínima e a
          iluminação pública (cerca de R$ 40), e deixa de pagar cerca de <strong className="text-white">{fmt(bill - 40)}</strong> por mês, em valores de hoje.
        </p>
      </div>
      <p className="mt-4 text-[11px] text-emerald-100/40 leading-relaxed">
        Estimativa com as médias da calculadora oficial da iGreen: mensalidade de cerca de 78% da conta no 1º ano, reajuste de 8,5% ao ano na conta de luz e de 5% na mensalidade (IPCA), mais cerca de R$ 40 de taxa mínima e iluminação pública. O valor exato sai na proposta.
      </p>
    </div>
  );
};

export default function Solar() {
  return (
    <PageShell testid="page-solar">
      <ServiceHero
        testid="solar-hero"
        overline="Conexão Solar · energia solar no seu telhado"
        title={["Placas solares no seu telhado", "sem pagar a instalação."]}
        sub="Você troca a conta de luz cheia por uma mensalidade menor que a de hoje, e no fim do contrato as placas são 100% suas. Projeto, equipamento, instalação e homologação por conta do fundo que financia o sistema."
        bullets={["R$ 0 de instalação", "Mensalidade menor que a conta", "100% das placas suas no fim", "Sem banco e sem entrada"]}
        primary={<CtaPrimary href="#simulador" external={false} testid="solar-simulate-cta">Simular a minha economia</CtaPrimary>}
        secondary={<CtaGhost href={waLink(WA_MSG)} testid="solar-whatsapp-cta">Falar no WhatsApp</CtaGhost>}
        image={IMAGES.rooftop}
      />

      <Section className="bg-[#061A14]" testid="solar-numeros">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ["R$ 0", "pela instalação e pelo equipamento"],
            ["Até 20%", "de desconto médio na conta de luz"],
            ["100%", "das placas suas no fim do contrato"],
            ["1º mês", "de economia já na primeira mensalidade"],
          ].map(([v, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 p-6 text-center">
                <p className="font-heading text-2xl sm:text-3xl font-extrabold text-[#00E676]">{v}</p>
                <p className="mt-2 text-xs text-emerald-100/55 leading-snug">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section testid="solar-simulador">
        <div id="simulador" className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>Simulador de economia</Overline>
            <SectionTitle>Quanto você economiza com <Accent>as placas no seu telhado</Accent>?</SectionTitle>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/55 leading-relaxed">
              Arraste até o valor médio da sua conta de luz e escolha o período. Você não paga a instalação: troca a
              conta cheia por uma mensalidade menor e, no fim do contrato, as placas são suas.
            </p>
            <div className="mt-8">
              <CtaPrimary href={waLink(WA_MSG)} testid="solar-proposal-cta">Quero a minha proposta grátis</CtaPrimary>
            </div>
          </Reveal>
          <Reveal delay={0.12}><SolarSimulator /></Reveal>
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="solar-o-que-e">
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Overline>O que é</Overline>
            <SectionTitle>O que é a <Accent>Conexão Solar</Accent></SectionTitle>
            <p className="mt-5 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Na Conexão Solar você não compra nada. Um fundo de investimento compra, instala e homologa as placas
              solares no seu imóvel. Durante o contrato, você paga ao fundo uma mensalidade calculada pelo seu consumo,
              sempre menor que a sua conta de luz de hoje.
            </p>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Quando o contrato termina (6, 8, 10 ou 12 anos, conforme a proposta), as placas passam a ser 100% suas,
              sem custo de transferência. A mensalidade acaba e você fica só com a taxa mínima da distribuidora.
            </p>
          </Reveal>
          <div className="grid gap-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-[#00E676]/25 bg-[#0B261E]/60 p-7">
                <h3 className="font-heading font-bold text-white">É para você se</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/60 leading-relaxed list-disc pl-5">
                  <li>Mora em casa, com telhado ou espaço no solo</li>
                  <li>A conta de luz está no seu CPF (ou você comprova o vínculo com o titular)</li>
                  <li>Tem de 21 a 70 anos e quer trocar a conta cheia por uma mensalidade menor</li>
                  <li>Consome até 3.500 kWh por mês</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-white/10 bg-[#0B261E]/40 p-7">
                <h3 className="font-heading font-bold text-white">Não é para você se</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/50 leading-relaxed list-disc pl-5">
                  <li>Mora em apartamento ou prédio</li>
                  <li>A conta é de empresa em CNPJ ou de média e alta tensão (Grupo A): veja a página de Empresas</li>
                  <li>Quer comprar o seu próprio sistema de placas solares</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section testid="solar-por-que">
        <Reveal>
          <Overline>Por que a Conexão Solar</Overline>
          <SectionTitle className="max-w-3xl">Suas próprias placas solares, <Accent>sem tirar dinheiro do bolso</Accent>.</SectionTitle>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="solar-features"
            items={[
              { icon: Sun, title: "Placas instaladas sem você pagar", text: "Um fundo de investimento compra, instala e homologa as placas no seu telhado. Você não paga nada na adesão nem pela instalação." },
              { icon: TrendingDown, title: "Mensalidade menor que a sua conta", text: "No lugar da conta de luz cheia, você paga uma mensalidade calculada pelo seu consumo, sempre menor que a sua fatura de hoje." },
              { icon: KeyRound, title: "No fim, as placas são 100% suas", text: "Terminado o contrato, as placas e o inversor passam para o seu nome, sem custo de transferência, e a mensalidade acaba." },
              { icon: Ban, title: "Sem banco e sem entrada", text: "A análise de crédito é feita pelo fundo, não por banco. Não tem financiamento bancário, entrada nem parcela de equipamento." },
              { icon: HardHat, title: "Equipe especializada cuida de tudo", text: "Projeto, visita técnica, instalação, homologação na distribuidora e troca do medidor pela distribuidora." },
              { icon: CalendarClock, title: "Só começa a pagar depois de ligada", text: "A primeira mensalidade vem só depois da troca do medidor e do primeiro ciclo de geração das placas." },
            ]}
          />
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12">
            <CompareTable
              testid="solar-compare"
              leftName="Conexão Solar iGreen"
              rightName="Comprar e financiar"
              rows={[
                ["Pagamento na adesão", "Nenhum", "Entrada ou valor à vista"],
                ["Equipamento e instalação", "Pagos pelo fundo, sem custo para você", "Por sua conta"],
                ["Aprovação", "Análise de crédito do fundo, sem banco", "Financiamento bancário"],
                ["Quanto você paga por mês", "Mensalidade menor que a conta de hoje", "Parcela do financiamento + conta"],
                ["Projeto e homologação", "Feitos pela equipe especializada", "Você contrata e acompanha"],
                ["Dono das placas", "Você, no fim do contrato", "Você, depois de pagar tudo"],
                ["Clube de benefícios", "iGreen Club grátis", "Não tem"],
              ]}
            />
          </div>
        </Reveal>
      </Section>

      <Section className="bg-[#061A14]" testid="solar-passos">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Passo a passo</Overline>
            <SectionTitle>Da conta de luz às <Accent>placas gerando</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="solar-steps"
            steps={[
              { n: "01", title: "Você me manda a conta de luz", text: "Com o seu consumo é gerada a proposta, com a mensalidade e o prazo." },
              { n: "02", title: "Análise de crédito do fundo", text: "Feita pelo fundo de investimento, sem banco e sem entrada." },
              { n: "03", title: "Visita técnica e contrato", text: "A engenharia avalia o telhado e o padrão; você assina o contrato digitalmente." },
              { n: "04", title: "Instalação e homologação", text: "Em até 100 dias depois da assinatura, com a troca do medidor pela distribuidora." },
              { n: "05", title: "Placas gerando, conta menor", text: "A primeira mensalidade só vem depois que as placas começam a gerar." },
            ]}
          />
        </div>
      </Section>

      <FaqBlock testid="solar-faq" title={<>Perguntas sobre <Accent>energia solar</Accent></>} items={FAQ} />

      <LeadForm
        overline="Vamos conversar?"
        title={<>Me conta o seu caso e eu te respondo <Accent>no WhatsApp</Accent>.</>}
        defaultInterest="Energia solar sem investimento"
      />
    </PageShell>
  );
}
